import * as THREE from 'three';
import { TilesRenderer } from '3d-tiles-renderer';
import { LatLonAlt } from '../../models/SiteAnchor';



// WGS84 ellipsoid parameters
const WGS84_A = 6378137.0; // Semi-major axis (meters)
const WGS84_B = 6356752.314245; // Semi-minor axis (meters)
const E_SQUARED = 1 - (WGS84_B * WGS84_B) / (WGS84_A * WGS84_A); // First eccentricity squared

// Function to convert ECEF (x, y, z) to WGS84 (latitude, longitude, height)
function ecefToWgs84(x: number, y: number, z: number): LatLonAlt {
  // Based on standard ECEF to geodetic conversion
  const p = Math.sqrt(x * x + y * y);
  if (p < 1e-10) { // Handle points near Earth's center
    return { lat: 0, lon: 0, alt: -WGS84_B };
  }
  const theta = Math.atan2(z * WGS84_A, p * WGS84_B);
  const eDashSquared = (WGS84_A * WGS84_A - WGS84_B * WGS84_B) / (WGS84_B * WGS84_B);
  const sinTheta = Math.sin(theta);
  const cosTheta = Math.cos(theta);

  const latitude = Math.atan2(
    z + eDashSquared * WGS84_B * sinTheta ** 3,
    p - E_SQUARED * WGS84_A * cosTheta ** 3
  );
  const longitude = Math.atan2(y, x);
  const N = WGS84_A / Math.sqrt(1 - E_SQUARED * Math.sin(latitude) ** 2);
  const height = p / Math.cos(latitude) - N;

  return {
    lat: (latitude * 180) / Math.PI, // Convert radians to degrees
    lon: (longitude * 180) / Math.PI, // Convert radians to degrees
    alt: height, // Height in meters
  };
}

// Function to convert WGS84 (latitude, longitude, height) to ECEF (x, y, z)
function wgs84ToEcef(lat: number, lon: number, alt: number): THREE.Vector3 {
  const latRad = (lat * Math.PI) / 180; // Convert degrees to radians
  const lonRad = (lon * Math.PI) / 180; // Convert degrees to radians

  const N = WGS84_A / Math.sqrt(1 - E_SQUARED * Math.sin(latRad) ** 2);
  const x = (N + alt) * Math.cos(latRad) * Math.cos(lonRad);
  const y = (N + alt) * Math.cos(latRad) * Math.sin(lonRad);
  const z = ((WGS84_B * WGS84_B) / (WGS84_A * WGS84_A) * N + alt) * Math.sin(latRad);

  return new THREE.Vector3(x, y, z);
}

// Function to convert a point from WGS84 to tileset coordinates
export function latLongToPoint(
  coords: LatLonAlt,
  planet: THREE.Object3D
): THREE.Vector3 {
  // Step 1: Convert WGS84 to ECEF
  const ecefPoint = wgs84ToEcef(coords.lat, coords.lon, coords.alt);

  // Step 2: Get the inverse of the tileset's root transform
  const rootTransform = new THREE.Matrix4();
  if (planet.matrixWorld) {
    rootTransform.copy(planet.matrixWorld); // World transform of the tileset
  } else {
    rootTransform.identity(); // Assume identity if no transform
  }
  const inverseTransform = new THREE.Matrix4().copy(rootTransform).invert();

  // Step 3: Transform ECEF point to tileset coordinates
  const tilesetPoint = ecefPoint.clone().applyMatrix4(inverseTransform);

  return tilesetPoint;
}

// Function to convert a point from tileset coordinates to WGS84
export function pointToLatLong(
  point: THREE.Vector3,
  planet: THREE.Object3D
): LatLonAlt {
  // Step 1: Get the tileset's root transform
  const rootTransform = new THREE.Matrix4();
  if (planet.matrixWorld) {
    rootTransform.copy(planet.matrixWorld); // World transform of the tileset
  } else {
    // If no transform is provided, assume identity (tileset at 0,0,0 in ECEF)
    rootTransform.identity();
  }

  // Step 2: Transform the point to ECEF
  let ecefPoint = point.clone();
  ecefPoint.applyMatrix4(rootTransform);

  // Step 3: Convert ECEF to WGS84
  return ecefToWgs84(ecefPoint.x, ecefPoint.y, ecefPoint.z);
}